import { LiveValue, ProblemError, type FlagsService, type HttpClient } from '@/model';
import { object } from './decode';
import type { SessionProtocol } from './protocol';

export const effectiveFlagDefaults = { prefetch: true, background_refresh: true, store_maintenance: true, status_line: true, 'projects.ui': false, 'projects.organize': false, 'agents.ui': false, 'assistants.anywhere': true, 'items.v1': false, 'calendar.v1': false, 'labels.v1': false, 'community.ui': true } as const;
const launchReads = { limits: '/v1/limits', claw_status: '/v1/claw/status', assistant: '/v1/assistant', read_aloud: '/v1/tts/capabilities', community_context: '/v1/community/context', item_context: '/v1/items/context' } as const;
export function createFlags(http: HttpClient, protocol: SessionProtocol): FlagsService & { refresh(): Promise<boolean>; reset(): void } {
  const flags = new LiveValue<Readonly<Record<string, unknown>>>({ ...effectiveFlagDefaults });
  const config = new LiveValue<Readonly<Record<string, unknown>> | undefined>(undefined);
  let generation = 0;
  let flight: Promise<boolean> | undefined;
  let configRevision = 0, configFlight: Promise<void> | undefined;
  let refusedFeatures = new Set<string>();
  const mergeConfig = (value: unknown, refused: Set<string>) => {
    const fresh = object(value), features = fresh.features;
    // A refusal probe may still reach stale discovery. Independent later discovery can restore support.
    const usable = features && typeof features === 'object' && !Array.isArray(features)
      ? Object.fromEntries(Object.entries(features).map(([key, value]) => [key, refused.has(key) ? false : value])) : features;
    return { ...config.get(), ...fresh, features: usable };
  };
  return {
    flags: () => flags, config: () => config,
    isEnabled: key => Boolean(flags.get()[key]),
    refreshConfig(invalidateFeatures = []) {
      invalidateFeatures.forEach(key => refusedFeatures.add(key));
      if (invalidateFeatures.length) config.set({ ...config.get(), features: { ...config.get()?.features as Record<string, unknown>, ...Object.fromEntries(invalidateFeatures.map(key => [key, false])) } });
      if (configFlight) return configFlight;
      const current = generation, revision = ++configRevision, refused = refusedFeatures;
      configFlight = http.get('/v1/config', { cause: 'refresh' }).then(value => {
        if (current === generation && revision === configRevision) config.set(mergeConfig(value, refused));
      }).finally(() => { if (current === generation) { configFlight = undefined; refusedFeatures = new Set(); } });
      return configFlight;
    },
    refresh() {
      if (flight) return flight;
      const current = generation, revision = configRevision, refused = refusedFeatures;
      const tasks = [http.get('/v1/flags', { protocol: 7, cause: 'refresh' }), http.get('/v1/config', { cause: 'refresh' }), ...Object.values(launchReads).map(path => http.get(path, { cause: 'background' }))];
      flight = Promise.allSettled(tasks).then(results => {
        if (current !== generation) return false;
        let changed = false;
        const flagResult = results[0];
        if (flagResult?.status === 'fulfilled') {
          try {
            const response = object(flagResult.value);
            const nextFlags = { ...effectiveFlagDefaults, ...object(response.flags ?? {}) };
            const server = response.server && typeof response.server === 'object' ? object(response.server) : {};
            changed = protocol.update(server.protocol);
            flags.set(nextFlags);
          } catch { /* Retain usable flags; a malformed peer must not block config settlement. */ }
        }
        const configResult = results[1];
        let next = config.get();
        if (configResult?.status === 'fulfilled' && revision === configRevision) {
          try { next = mergeConfig(configResult.value, refused); }
          catch { /* Decoder failures use the same fallback as a failed config read. */ }
        }
        const extras: Record<string, unknown> = {};
        Object.keys(launchReads).forEach((key, i) => { const result = results[i + 2]; if (result?.status === 'fulfilled') {
          // community/context wraps ContextDTO; items/context is already a raw discovery DTO.
          // Keep the other startup endpoints' wire shapes (limits, assistant, status and TTS capabilities).
          try { extras[key] = key === 'community_context' ? object(object(result.value).context) : result.value; }
          catch { /* A malformed context cannot replace this login's last usable context. */ }
        }
          else if (result?.status === 'rejected' && result.reason instanceof ProblemError && [401, 403, 404, 410].includes(result.reason.status)) extras[key] = undefined; });
        // Undefined means pending; a failed startup still settles to an empty config.
        config.set({ ...next, ...extras });
        return changed;
      }).catch(() => false).finally(() => { if (current === generation) flight = undefined; });
      return flight;
    },
    reset() { generation++; configRevision++; flight = undefined; configFlight = undefined; refusedFeatures = new Set(); flags.set({ ...effectiveFlagDefaults }); config.set(undefined); },
  };
}
